
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  Dropdown,
  DropdownOption,
  DropdownTitle,
  DropdownSeparator,
  DropdownLink,
  Position,
  Button,
  Category,
  Avatar,
  Margin,
  Size,
  Text,
  Tier,
  Icon,
  RoundIcon
} from '@drawbotics/react-drylus';
import Playground, { PropsTable } from '~/components/Playground';
import testSVG from '~/assets/coding-guidelines.svg';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Dropdown`}</h1>
<p>{`A little floating menu used to perform actions. Can also contain some information, but should be kept at a minimum. If you see that the content of the dropdown is becoming excessive, use a `}<a parentName="p" {...{
        "href": "/component-kit/components/modal"
      }}><inlineCode parentName="a">{`Modal`}</inlineCode></a>{` instead.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { Dropdown, DropdownOption, DropdownTitle, DropdownSeparator } from '@drawbotics/react-drylus';
`}</code></pre>
<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<Dropdown trigger={
  <Button>I show the menu</Button>
}>
  <DropdownOption text="First option" />
  <DropdownOption text="Second option" />
  <DropdownOption text="Third option" disabled />
  <DropdownSeparator />
  <DropdownOption text="Fourth option" />
</Dropdown>`} component={Dropdown} mdxType="Playground">
  <Dropdown trigger={<Button mdxType="Button">I show the menu</Button>} mdxType="Dropdown">
    <DropdownOption text="First option" mdxType="DropdownOption" />
    <DropdownOption text="Second option" mdxType="DropdownOption" />
    <DropdownOption text="Third option" disabled mdxType="DropdownOption" />
    <DropdownSeparator mdxType="DropdownSeparator" />
    <DropdownOption text="Fourth option" mdxType="DropdownOption" />
  </Dropdown>
</Playground>
<h4>{`Dropdown option`}</h4>
<Playground __position={1} __code={`<DropdownOption text="I'm an option" />`} component={DropdownOption} mdxType="Playground">
  <DropdownOption text="I'm an option" mdxType="DropdownOption" />
</Playground>
<h4>{` Dropdown title`}</h4>
<PropsTable component={DropdownTitle} mdxType="PropsTable" />
<h4>{` Dropdown link`}</h4>
<p>{`Extends the `}<inlineCode parentName="p">{`DropdownOption`}</inlineCode>{` props but adds `}<inlineCode parentName="p">{`href`}</inlineCode>{` and `}<inlineCode parentName="p">{`linkComponent`}</inlineCode></p>
<PropsTable component={DropdownLink} mdxType="PropsTable" />
<h3>{`Example with categories & leading`}</h3>
<Playground __position={4} __code={`<Dropdown trigger={<Avatar text="MK" />} side={Position.RIGHT}>
  <DropdownOption text="Alright option" category={Category.SUCCESS} />
  <DropdownOption text="Not okay" category={Category.DANGER} leading={<Icon name="alert-circle" />} />
  <DropdownOption text="Maybe okay" category={Category.WARNING} />
  <DropdownOption text="Option with RoundIcon" leading={<RoundIcon name="heart" />} />
  <DropdownOption text="And an Avatar" leading={<Avatar text="TT" />} />
  <DropdownOption text="Custom SVG fits too" leading={<img src={testSVG} style={{ height: '20px' }}/>} />
</Dropdown>`} mdxType="Playground">
  <Dropdown trigger={<Avatar text="MK" mdxType="Avatar" />} side={Position.RIGHT} mdxType="Dropdown">
    <DropdownOption text="Alright option" category={Category.SUCCESS} mdxType="DropdownOption" />
    <DropdownOption text="Not okay" category={Category.DANGER} leading={<Icon name="alert-circle" mdxType="Icon" />} mdxType="DropdownOption" />
    <DropdownOption text="Maybe okay" category={Category.WARNING} mdxType="DropdownOption" />
    <DropdownOption text="Option with RoundIcon" leading={<RoundIcon name="heart" mdxType="RoundIcon" />} mdxType="DropdownOption" />
    <DropdownOption text="And an Avatar" leading={<Avatar text="TT" mdxType="Avatar" />} mdxType="DropdownOption" />
    <DropdownOption text="Custom SVG fits too" leading={<img src={testSVG} style={{
          height: '20px'
        }} />} mdxType="DropdownOption" />
  </Dropdown>
</Playground>
<h3>{`Example with titles`}</h3>
<Playground __position={5} __code={`<Dropdown trigger={<Avatar text="MK" />} side={Position.TOP}>
  <DropdownTitle text="First section" />
  <DropdownOption text="First option" />
  <DropdownOption text="Second option" />
  <DropdownOption text="Third option" />
  <DropdownTitle text="Second section" />
  <DropdownOption text="First option" />
  <DropdownOption text="Second option" disabled />
  <DropdownOption text="Third option" />
</Dropdown>`} mdxType="Playground">
  <Dropdown trigger={<Avatar text="MK" mdxType="Avatar" />} side={Position.TOP} mdxType="Dropdown">
    <DropdownTitle text="First section" mdxType="DropdownTitle" />
    <DropdownOption text="First option" mdxType="DropdownOption" />
    <DropdownOption text="Second option" mdxType="DropdownOption" />
    <DropdownOption text="Third option" mdxType="DropdownOption" />
    <DropdownTitle text="Second section" mdxType="DropdownTitle" />
    <DropdownOption text="First option" mdxType="DropdownOption" />
    <DropdownOption text="Second option" disabled mdxType="DropdownOption" />
    <DropdownOption text="Third option" mdxType="DropdownOption" />
  </Dropdown>
</Playground>
<h3>{`Example with links`}</h3>
<p>{`Options in the dropdown can also be links, when using DropdownLink`}</p>
<Playground __position={6} __code={`<Dropdown trigger={<Avatar text="JD" />} side={Position.RIGHT}>
  <DropdownLink text="Settings" href="" />
  <DropdownOption text="Logout" />
</Dropdown>`} mdxType="Playground">
  <Dropdown trigger={<Avatar text="JD" mdxType="Avatar" />} side={Position.RIGHT} mdxType="Dropdown">
    <DropdownLink text="Settings" href="" mdxType="DropdownLink" />
    <DropdownOption text="Logout" mdxType="DropdownOption" />
  </Dropdown>
</Playground>
<h3>{`Example with custom content`}</h3>
<p>{`You can also put whatever you want in a dropdown, as long as it respects the design system`}</p>
<Playground __position={7} __code={`<Dropdown trigger={<Avatar text="JD" />} side={Position.RIGHT}>
  <Margin size={{ top: Size.EXTRA_SMALL, right: Size.SMALL, left: Size.SMALL }}>
    <Text style={{ whiteSpace: 'nowrap' }}>John Doe</Text>
  </Margin>
  <Margin size={{ right: Size.SMALL, left: Size.SMALL }}>
    <Text tier={Tier.SECONDARY} size={Size.SMALL}>Drawer</Text>
  </Margin>
  <DropdownSeparator />
  <DropdownOption text="Settings" />
  <DropdownOption text="Logout" />
</Dropdown>`} mdxType="Playground">
  <Dropdown trigger={<Avatar text="JD" mdxType="Avatar" />} side={Position.RIGHT} mdxType="Dropdown">
    <Margin size={{
          top: Size.EXTRA_SMALL,
          right: Size.SMALL,
          left: Size.SMALL
        }} mdxType="Margin">
      <Text style={{
            whiteSpace: 'nowrap'
          }} mdxType="Text">John Doe</Text>
    </Margin>
    <Margin size={{
          right: Size.SMALL,
          left: Size.SMALL
        }} mdxType="Margin">
      <Text tier={Tier.SECONDARY} size={Size.SMALL} mdxType="Text">Drawer</Text>
    </Margin>
    <DropdownSeparator mdxType="DropdownSeparator" />
    <DropdownOption text="Settings" mdxType="DropdownOption" />
    <DropdownOption text="Logout" mdxType="DropdownOption" />
  </Dropdown>
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;